/**
 * RFC 9457 Problem Details, as the client sees them.
 *
 * The `code` is the stable machine contract. Clients switch on it and never
 * parse `detail`, which is human text and may change without notice.
 * docs/06-api-design.md §5
 */

export interface ProblemDetail {
  type: string;
  title: string;
  status: number;
  code: string;
  detail: string;
  instance: string;
  request_id?: string;
  errors?: FieldError[];
  [key: string]: unknown;
}

export interface FieldError {
  field: string;
  code: string;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId?: string;
  readonly fieldErrors: FieldError[];
  readonly problem: ProblemDetail;

  constructor(problem: ProblemDetail) {
    super(problem.detail || problem.title);
    this.name = "ApiError";
    this.status = problem.status;
    this.code = problem.code;
    this.requestId = problem.request_id;
    this.fieldErrors = problem.errors ?? [];
    this.problem = problem;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** Plan limit reached or the school's subscription is inactive. */
  get isPaymentRequired(): boolean {
    return this.status === 402;
  }

  get isRetryable(): boolean {
    return this.status >= 500 || this.status === 429;
  }
}

export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super("Could not reach the server. Check your connection and try again.");
    this.name = "NetworkError";
    this.cause = cause;
  }
}

/**
 * Copy for the error codes the UI actually surfaces.
 *
 * Anything unmapped falls back to the server's `detail`, and unmapped codes are
 * reported so the gap gets closed rather than silently shown as raw API text.
 * A generated union from the backend's code registry replaces this hand-written
 * map when the codegen pipeline lands. docs/06-api-design.md §12
 */
const ERROR_COPY: Record<string, string> = {
  "auth.invalid_credentials": "That email/phone or password is incorrect.",
  "auth.account_locked": "Too many failed attempts. Please try again in a few minutes.",
  "auth.account_disabled": "This account has been disabled. Contact your school office.",
  "auth.no_active_membership": "This account is not linked to an active school.",
  "auth.membership_unavailable": "That school is not available for this account.",
  "auth.session_revoked": "Your session ended for security reasons. Please sign in again.",
  "auth.token_expired": "Your session expired. Please sign in again.",
  "auth.step_up_required": "Please re-enter your password to continue.",
  "authz.permission_denied": "You do not have permission to do that.",
  "tenant.inactive": "Your school's subscription is not active. Please contact the office.",
  "entitlement.quota_exceeded": "Your school's plan limit has been reached.",
  "rate_limit.exceeded": "Too many requests. Please wait a moment and try again.",
  "request.validation_failed": "Please check the highlighted fields.",
  "resource.not_found": "We couldn't find that.",
  "resource.conflict": "Someone else changed this first. Reload and try again.",
  "internal.error": "Something went wrong on our side. Please try again.",
};

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return ERROR_COPY[error.code] ?? error.message;
  }
  if (error instanceof NetworkError) {
    return error.message;
  }
  return "Something went wrong. Please try again.";
}
